import React, { useState, useEffect } from 'react';

import OpenProductionTasksWarning from '../components/OpenProductionTasksWarning';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STAGES = [
  { key:'cutting', label:'Cutting' },
  { key:'webbing', label:'Webbing' },
  { key:'handle_preparation', label:'Handle Preparation' },
  { key:'sewing', label:'Sewing' },
  { key:'turning', label:'Turning' },
  { key:'printing', label:'Printing' },
  { key:'finishing', label:'Finishing' },
  { key:'packing', label:'Packing' },
];

const STAGE_COLORS = {
  cutting:'#EDF4FF', webbing:'#F3EEFF', handle_preparation:'#FFF3DC',
  sewing:'#E8F5EB', turning:'#FFF3DC', printing:'#E0F5F5',
  finishing:'#F2EFE7', packing:'#E8F5EB', qc:'#FDEAEA'
};

export default function ProductionCounts() {
  const { user } = useAuth();
  const canSubmit = ['super_admin','production_team_lead','production_supervisor','inventory_officer'].includes(user?.role);
  const canVerify = ['super_admin','inventory_officer'].includes(user?.role);
  const canCompleteQc = ['super_admin','production_team_lead'].includes(user?.role);

  const [jobOrders, setJobOrders] = useState([]);
  const [selectedJO, setSelectedJO] = useState(null);
  const [counts, setCounts] = useState([]);
  const [stageTotals, setStageTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showQcForm, setShowQcForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [workflowSaving, setWorkflowSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [qcForm, setQcForm] = useState({
    qc_note:'',
    packaging_note:''
  });
  const [form, setForm] = useState({
    production_stage:'cutting', quantity_completed:'',
    rework_count:0, issues:'', count_date:'', count_time:'',
    team_members:''
  });

  const loadSummary = async (selectedId = null) => {
    const data = await get('/production-counts/summary');
    const jobs = data.data || [];

    setJobOrders(jobs);

    if (selectedId) {
      const refreshedJob =
        jobs.find(job => job.id === selectedId);

      setSelectedJO(refreshedJob || null);

      if (!refreshedJob) {
        setCounts([]);
        setStageTotals({});
      }
    }

    return jobs;
  };

  useEffect(() => {
    loadSummary()
      .catch(error => alert(error.message))
      .finally(() => setLoading(false));
  }, []);

  const loadCounts = (joId) => {
    get('/production-counts/job-order/'+joId).then(d => {
      setCounts(d.data||[]);
      setStageTotals(d.stage_totals||{});
    });
  };

  const selectJO = (jo) => {
    setSelectedJO(jo);
    loadCounts(jo.id);
  };

  const submitCount = async () => {
    const submittedQuantity =
      Number(form.quantity_completed);

    const alreadyRecorded =
      Number(
        stageTotals[
          form.production_stage
        ] || 0
      );

    const requiredQuantity =
      Number(selectedJO?.quantity || 0);

    const remainingAllowed =
      Math.max(
        0,
        requiredQuantity -
        alreadyRecorded
      );

    if (
      !Number.isInteger(
        submittedQuantity
      ) ||
      submittedQuantity <= 0
    ) {
      alert(
        'Quantity must be a positive whole number.'
      );
      return;
    }

    if (
      submittedQuantity >
      remainingAllowed
    ) {
      alert(
        'This count exceeds the remaining quantity. Only ' +
        remainingAllowed +
        ' unit' +
        (remainingAllowed === 1 ? '' : 's') +
        ' may be recorded for this stage.'
      );
      return;
    }

    setSaving(true);
    try {
      const result = await post('/production-counts/job-order/'+selectedJO.id, {
        ...form,
        quantity_completed:
          submittedQuantity,
        team_members: form.team_members ? form.team_members.split(',').map(s=>s.trim()) : []
      });

      if (result?.job_order_status) {
        setSelectedJO(current =>
          current
            ? {
                ...current,
                status:
                  result.job_order_status
              }
            : current
        );
      }

      // Upload photo if provided
      if (photoFile) {
        const fd = new FormData();
        fd.append('photo', photoFile);
        const token = localStorage.getItem('bb_token');
        await fetch('/api/production-counts/'+result.id+'/photo', {
          method:'POST',
          headers:{ Authorization:'Bearer '+token },
          body: fd
        });
      }

      setShowForm(false);
      setForm({production_stage:'cutting',quantity_completed:'',
        rework_count:0,issues:'',count_date:'',count_time:'',team_members:''});
      setPhotoFile(null);
      loadCounts(selectedJO.id);
      await loadSummary(selectedJO.id);
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const verifyCount = async (countId) => {
    if (
      selectedJO?.status !==
      'awaiting_inventory_verification'
    ) {
      alert(
        'Production Team Lead must complete QC and packaging before Inventory verification.'
      );
      return;
    }

    if (
      !window.confirm(
        'Verify this production count? This confirms the finished goods were physically counted and checked.'
      )
    ) return;

    setWorkflowSaving(true);

    try {
      const result =
        await put(
          '/production-counts/' +
            countId +
            '/verify',
          {}
        );

      alert(
        result?.message ||
        'Production count verified successfully.'
      );

      await loadSummary(selectedJO.id);

      const remainingJobs =
        await get('/production-counts/summary');

      const stillActive =
        (remainingJobs.data || [])
          .some(job => job.id === selectedJO.id);

      if (stillActive) {
        loadCounts(selectedJO.id);
      }
    } catch(err) {
      alert(err.message);
    } finally {
      setWorkflowSaving(false);
    }
  };

  const completeQcPackaging = async () => {
    if (
      selectedJO?.status !==
      'awaiting_qc_packaging'
    ) {
      alert(
        'This Job Order is not awaiting QC and packaging.'
      );
      return;
    }

    if (!qcForm.qc_note.trim()) {
      alert('Enter the QC findings.');
      return;
    }

    if (!qcForm.packaging_note.trim()) {
      alert('Enter the packaging confirmation.');
      return;
    }

    if (
      !window.confirm(
        'Confirm that QC has passed and packaging is complete? The Job Order will move to Inventory for independent verification.'
      )
    ) return;

    setWorkflowSaving(true);

    try {
      const result =
        await post(
          '/production-counts/' +
            selectedJO.id +
            '/qc-packaging/complete',
          {
            qc_note:
              qcForm.qc_note.trim(),
            packaging_note:
              qcForm.packaging_note.trim()
          }
        );

      alert(
        result?.message ||
        'QC and packaging completed. Inventory may now verify the finished goods.'
      );

      setShowQcForm(false);
      setQcForm({
        qc_note:'',
        packaging_note:''
      });

      await loadSummary(selectedJO.id);
      loadCounts(selectedJO.id);
    } catch(err) {
      alert(err.message);
    } finally {
      setWorkflowSaving(false);
    }
  };

  const getProgress = (jo) => {
    if (!jo.quantity || jo.quantity === 0) return 0;
    return Math.min(100, Math.round((jo.packed / jo.quantity) * 100));
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">🔨 Production Counts</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Track and verify production progress per job order
          </p>
        </div>
        {selectedJO && (
          <button className="btn btn-ghost" onClick={()=>{setSelectedJO(null);setCounts([]);}}>
            ← Back to All Jobs
          </button>
        )}
      </div>

      {loading ? <div className="spinner"></div> : !selectedJO ? (
        // Job Orders List
        <div>
          <div style={{fontSize:11,fontWeight:600,color:'#556B2F',textTransform:'uppercase',
            letterSpacing:'.08em',marginBottom:12}}>Active Job Orders</div>
          {jobOrders.length === 0 ? (
            <div className="empty-state">
              <h3>No active job orders</h3>
              <p>Production counts will appear here when job orders are in progress</p>
            </div>
          ) : jobOrders.map(jo => {
            const progress = getProgress(jo);
            return (
              <div key={jo.id} style={{background:'#fff',border:'1px solid var(--border-light)',
                borderRadius:10,padding:16,marginBottom:10,cursor:'pointer'}}
                onClick={()=>selectJO(jo)}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                      <span style={{fontFamily:'monospace',fontSize:12,fontWeight:600,
                        color:'#556B2F'}}>{jo.job_number}</span>
                      <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                        background:'#FFF3DC',color:'#8B5E0A',textTransform:'uppercase'}}>
                        {jo.status?.replace(/_/g,' ')}
                      </span>
                    </div>
                    <div style={{fontSize:13,fontWeight:600,color:'#3B2A21',marginBottom:4}}>
                      {jo.product_description}
                    </div>
                    <div style={{fontSize:11,color:'#8A8A8A'}}>
                      Qty: {jo.quantity} · Packed: {jo.packed} · Entries: {jo.total_entries}
                      {jo.last_count_date && <span> · Last count: {jo.last_count_date}</span>}
                    </div>
                  </div>
                  <div style={{textAlign:'right',minWidth:80}}>
                    <div style={{fontSize:14,fontWeight:700,color:'#556B2F'}}>{progress}%</div>
                    <div style={{width:80,height:6,background:'#EDE7D8',borderRadius:3,marginTop:4}}>
                      <div style={{width:progress+'%',height:6,
                        background:progress===100?'#2E6B3A':'#556B2F',borderRadius:3}}></div>
                    </div>
                    <div style={{fontSize:9,color:'#8A8A8A',marginTop:2}}>packed</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Job Order Detail
        <div>
          {/* Header */}
          <div style={{background:'#3B2A21',borderRadius:12,padding:'16px 20px',
            marginBottom:20,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <div style={{fontSize:12,color:'rgba(242,239,231,.5)',marginBottom:2}}>
                {selectedJO.job_number}
              </div>
              <div style={{fontSize:16,fontWeight:600,color:'#A8893D'}}>
                {selectedJO.product_description}
              </div>
              <div style={{fontSize:11,color:'rgba(242,239,231,.6)',marginTop:4}}>
                Total Required: {selectedJO.quantity} units
              </div>
            </div>
            <div style={{
              display:'flex',
              gap:8,
              flexWrap:'wrap',
              justifyContent:'flex-end'
            }}>
              {canCompleteQc &&
                selectedJO.status ===
                  'awaiting_qc_packaging' && (
                <button
                  className="btn btn-primary"
                  style={{
                    background:'#A8893D',
                    borderColor:'#A8893D',
                    color:'#3B2A21'
                  }}
                  onClick={() =>
                    setShowQcForm(true)
                  }
                  disabled={workflowSaving}
                >
                  ✓ Complete QC & Packaging
                </button>
              )}

              {canSubmit &&
                ![
                  'awaiting_qc_packaging',
                  'awaiting_inventory_verification',
                  'ready',
                  'dispatched',
                  'delivered',
                  'cancelled'
                ].includes(selectedJO.status) && (
                <button
                  className="btn btn-primary"
                  onClick={() =>
                    setShowForm(true)
                  }
                >
                  + Record Count
                </button>
              )}
            </div>
          </div>

          {/* Finished-goods release workflow */}
          <div style={{
            background:
              selectedJO.status === 'awaiting_qc_packaging'
                ? '#FFF3DC'
                : selectedJO.status === 'awaiting_inventory_verification'
                  ? '#EDF4FF'
                  : '#F2EFE7',
            border:
              selectedJO.status === 'awaiting_qc_packaging'
                ? '1px solid #E7C77A'
                : selectedJO.status === 'awaiting_inventory_verification'
                  ? '1px solid #AFC7ED'
                  : '1px solid var(--border-light)',
            borderRadius:10,
            padding:'12px 14px',
            marginBottom:20
          }}>
            <div style={{
              fontSize:12,
              fontWeight:700,
              color:'#3B2A21',
              marginBottom:4
            }}>
              Finished-Goods Release Stage
            </div>

            <div style={{
              fontSize:12,
              color:'#555',
              lineHeight:1.5
            }}>
              {selectedJO.status ===
                'awaiting_qc_packaging'
                ? 'Packing is complete. The Production Team Lead must inspect quality and confirm packaging before Inventory can verify the goods.'
                : selectedJO.status ===
                    'awaiting_inventory_verification'
                  ? 'QC and packaging are complete. Inventory must now physically count and verify every outstanding production count. The delivery will be created automatically after final verification.'
                  : 'Production counts are still in progress. Complete the packing quantity before the finished-goods release process begins.'}
            </div>

            <div style={{
              display:'flex',
              gap:6,
              flexWrap:'wrap',
              marginTop:10
            }}>
              {[
                {
                  label:'1. Packing',
                  complete:[
                    'awaiting_qc_packaging',
                    'awaiting_inventory_verification'
                  ].includes(selectedJO.status)
                },
                {
                  label:'2. QC & Packaging',
                  complete:
                    selectedJO.status ===
                    'awaiting_inventory_verification'
                },
                {
                  label:'3. Inventory Verification',
                  complete:false
                },
                {
                  label:'4. Delivery Created',
                  complete:false
                }
              ].map(step => (
                <span
                  key={step.label}
                  style={{
                    fontSize:10,
                    fontWeight:600,
                    padding:'4px 8px',
                    borderRadius:20,
                    background:
                      step.complete
                        ? '#E8F5EB'
                        : '#fff',
                    color:
                      step.complete
                        ? '#2E6B3A'
                        : '#8A8A8A',
                    border:
                      '1px solid rgba(0,0,0,.08)'
                  }}
                >
                  {step.complete ? '✓ ' : ''}
                  {step.label}
                </span>
              ))}
            </div>
          </div>

          {/* Stage Summary */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',
            gap:8,marginBottom:20}}>
            {STAGES.map(stage => (
              <div key={stage.key} style={{background:STAGE_COLORS[stage.key]||'#F9F9F9',
                borderRadius:8,padding:'10px 12px',textAlign:'center'}}>
                <div style={{fontSize:11,fontWeight:600,color:'#3B2A21',marginBottom:4}}>
                  {stage.label}
                </div>
                <div style={{fontSize:20,fontWeight:700,color:'#556B2F'}}>
                  {stageTotals[stage.key]||0}
                </div>
                <div style={{fontSize:9,color:'#8A8A8A'}}>of {selectedJO.quantity}</div>
              </div>
            ))}
          </div>

          {/* Count Entries */}
          <div style={{fontSize:11,fontWeight:600,color:'#556B2F',textTransform:'uppercase',
            letterSpacing:'.08em',marginBottom:12}}>Count History</div>

          {counts.length === 0 ? (
            <div className="empty-state">
              <h3>No counts recorded yet</h3>
              {canSubmit && <p>Click + Record Count to start tracking production</p>}
            </div>
          ) : counts.map(count => (
            <div key={count.id} style={{background:'#fff',border:'1px solid var(--border-light)',
              borderRadius:10,padding:14,marginBottom:8}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                    <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,
                      background:STAGE_COLORS[count.production_stage]||'#F9F9F9',
                      color:'#3B2A21',textTransform:'uppercase'}}>
                      {count.production_stage?.replace(/_/g,' ')}
                    </span>
                    {count.verified_by ? (
                      <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                        background:'#E8F5EB',color:'#2E6B3A'}}>✓ Verified</span>
                    ) : (
                      <span style={{fontSize:9,padding:'2px 8px',borderRadius:20,
                        background:'#FFF3DC',color:'#8B5E0A'}}>Unverified</span>
                    )}
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:6}}>
                    <div>
                      <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase'}}>This Count</div>
                      <div style={{fontSize:18,fontWeight:700,color:'#556B2F'}}>{count.quantity_completed}</div>
                    </div>
                    <div>
                      <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase'}}>Cumulative</div>
                      <div style={{fontSize:18,fontWeight:700,color:'#3B2A21'}}>{count.cumulative_completed}</div>
                    </div>
                    <div>
                      <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase'}}>Remaining</div>
                      <div style={{fontSize:18,fontWeight:700,color:count.quantity_remaining===0?'#2E6B3A':'#8F2E24'}}>
                        {count.quantity_remaining}
                      </div>
                    </div>
                  </div>
                  <div style={{fontSize:11,color:'#8A8A8A'}}>
                    By {count.counted_by_name} · {count.count_date} {count.count_time?.slice(0,5)}
                    {count.rework_count > 0 && <span style={{color:'#8F2E24'}}> · Rework: {count.rework_count}</span>}
                  </div>
                  {count.issues && (
                    <div style={{fontSize:11,color:'#8F2E24',marginTop:4}}>⚠ {count.issues}</div>
                  )}
                  {count.team_members?.length > 0 && (
                    <div style={{fontSize:11,color:'#8A8A8A',marginTop:4}}>
                      Team: {Array.isArray(count.team_members) ? count.team_members.join(', ') : count.team_members}
                    </div>
                  )}
                  {count.verified_by_name && (
                    <div style={{fontSize:11,color:'#2E6B3A',marginTop:4}}>
                      ✓ Verified by {count.verified_by_name} on {new Date(count.verified_at).toLocaleDateString('en-NG')}
                    </div>
                  )}
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:6,marginLeft:12}}>
                  {count.photo_url && (
                    <a href={count.photo_url} target="_blank" rel="noreferrer"
                      style={{fontSize:11,color:'#556B2F'}}>📷 Photo</a>
                  )}
                  {canVerify &&
                    !count.verified_by &&
                    selectedJO.status ===
                      'awaiting_inventory_verification' && (
                    <button
                      className="btn btn-outline btn-sm"
                      style={{
                        color:'#2E6B3A',
                        borderColor:'#2E6B3A',
                        fontSize:11
                      }}
                      onClick={() =>
                        verifyCount(count.id)
                      }
                      disabled={workflowSaving}
                    >
                      {workflowSaving
                        ? 'Verifying...'
                        : 'Verify'}
                    </button>
                  )}

                  {canVerify &&
                    !count.verified_by &&
                    selectedJO.status ===
                      'awaiting_qc_packaging' && (
                    <span style={{
                      fontSize:10,
                      color:'#8B5E0A',
                      maxWidth:120,
                      textAlign:'right'
                    }}>
                      Waiting for Production Lead QC
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

            {selectedJO && (
        <OpenProductionTasksWarning
          jobOrderId={selectedJO.id}
          jobOrderStatus={selectedJO.status}
        />
      )}

      {/* QC and Packaging Completion Form */}
      {showQcForm && selectedJO && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{maxWidth:520}}
          >
            <div className="modal-hdr">
              <span className="modal-title">
                Complete QC & Packaging
              </span>

              <button
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  setShowQcForm(false)
                }
                disabled={workflowSaving}
              >
                X
              </button>
            </div>

            <div className="modal-body">
              <div style={{
                background:'#FFF3DC',
                borderRadius:8,
                padding:'10px 12px',
                marginBottom:16,
                fontSize:12,
                color:'#8B5E0A',
                lineHeight:1.5
              }}>
                Job Order:
                {' '}
                <strong>
                  {selectedJO.job_number}
                </strong>
                <br />
                Confirm that the finished goods passed
                quality checks and that packaging is
                complete before releasing them to
                Inventory.
              </div>

              <div className="form-group">
                <label className="form-label">
                  QC Findings *
                </label>

                <textarea
                  className="form-control"
                  rows={3}
                  value={qcForm.qc_note}
                  onChange={event =>
                    setQcForm({
                      ...qcForm,
                      qc_note:event.target.value
                    })
                  }
                  placeholder="State what was checked and confirm that the finished goods passed QC."
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Packaging Confirmation *
                </label>

                <textarea
                  className="form-control"
                  rows={3}
                  value={
                    qcForm.packaging_note
                  }
                  onChange={event =>
                    setQcForm({
                      ...qcForm,
                      packaging_note:
                        event.target.value
                    })
                  }
                  placeholder="Confirm quantity, packaging condition, labels and any special packaging instructions."
                />
              </div>
            </div>

            <div className="modal-ftr">
              <button
                className="btn btn-outline"
                onClick={() =>
                  setShowQcForm(false)
                }
                disabled={workflowSaving}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={
                  completeQcPackaging
                }
                disabled={workflowSaving}
              >
                {workflowSaving
                  ? 'Completing...'
                  : 'Confirm QC & Packaging'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Count Form */}
      {showForm && selectedJO && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-hdr">
              <span className="modal-title">Record Production Count</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#F2EFE7',borderRadius:8,padding:'8px 12px',
                marginBottom:16,fontSize:12,color:'#556B2F'}}>
                Job Order: <strong>{selectedJO.job_number}</strong> · Total Required: <strong>{selectedJO.quantity}</strong>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Production Stage *</label>
                  <select className="form-control" value={form.production_stage}
                    onChange={e=>setForm({...form,production_stage:e.target.value})}>
                    {STAGES.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Quantity Completed *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={
                      form.quantity_completed
                    }
                    onChange={event =>
                      setForm({
                        ...form,
                        quantity_completed:
                          event.target.value
                      })
                    }
                    min="1"
                    max={Math.max(
                      0,
                      Number(
                        selectedJO.quantity ||
                        0
                      ) -
                      Number(
                        stageTotals[
                          form.production_stage
                        ] || 0
                      )
                    )}
                    step="1"
                    placeholder="0"
                  />
                  <div style={{
                    fontSize:10,
                    color:'#8A8A8A',
                    marginTop:4
                  }}>
                    Remaining allowed:
                    {' '}
                    {Math.max(
                      0,
                      Number(
                        selectedJO.quantity ||
                        0
                      ) -
                      Number(
                        stageTotals[
                          form.production_stage
                        ] || 0
                      )
                    )}
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Count Date</label>
                  <input type="date" className="form-control" value={form.count_date}
                    onChange={e=>setForm({...form,count_date:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Count Time</label>
                  <input type="time" className="form-control" value={form.count_time}
                    onChange={e=>setForm({...form,count_time:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Rework Count</label>
                  <input type="number" className="form-control" value={form.rework_count}
                    onChange={e=>setForm({...form,rework_count:e.target.value})}
                    min="0" placeholder="0" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Team Members (comma separated)</label>
                <input className="form-control" value={form.team_members}
                  onChange={e=>setForm({...form,team_members:e.target.value})}
                  placeholder="e.g. Roland, Tunde, Amaka" />
              </div>
              <div className="form-group">
                <label className="form-label">Issues / Notes</label>
                <textarea className="form-control" rows={2} value={form.issues}
                  onChange={e=>setForm({...form,issues:e.target.value})}
                  placeholder="Any issues, delays or rework reasons..." />
              </div>
              <div className="form-group">
                <label className="form-label">Photo Evidence (optional)</label>
                <input type="file" className="form-control" accept=".jpg,.jpeg,.png"
                  onChange={e=>setPhotoFile(e.target.files[0])} />
                {photoFile && <div style={{fontSize:11,color:'#556B2F',marginTop:4}}>
                  📷 {photoFile.name}
                </div>}
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitCount} disabled={saving}>
                {saving?'Saving...':'Record Count'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
